import * as Sentry from '@sentry/react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { Auth0Provider, Auth0ProviderOptions } from '@auth0/auth0-react'
import { LicenseInfo } from '@mui/x-license'

import ErrorPage from 'src/components/error-page'
import { store } from 'src/store/store'
import App from './App'
import theme from './theme'
import './index.css'

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY || '')

let isProd =
  !(import.meta.env.VITE_FRONTEND_URL || 'test').includes('test') &&
  !import.meta.env.VITE_FRONTEND_URL.includes('localhost')

if (isProd) {
  Sentry.init({
    dsn: 'https://nnMKmzTA6Q7o1V5Ae7xamPho@s2306061.us-east-9.betterstackdata.com/2306126',
    release: import.meta.env.VITE_GIT_RELEASE_VERSION || '1.0.0',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

let router = createBrowserRouter([
  {
    path: '/*',
    element: <App />,
    errorElement: <ErrorPage />,
  },
])

let auth0ProviderConfig: Auth0ProviderOptions = {
  domain: import.meta.env.VITE_AUTH0_DOMAIN,
  clientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
  authorizationParams: {
    redirect_uri: `${window.location.origin}/callback`,
    audience: import.meta.env.VITE_AUTH0_AUDIENCE,
    scope: 'openid profile email offline_access',
  },
  cacheLocation: 'localstorage',
  useRefreshTokens: true,
  onRedirectCallback: (appState) => {
    // Store the returnTo path in sessionStorage so the callback component can access it
    if (appState?.returnTo) {
      sessionStorage.setItem('auth_return_to', appState.returnTo)
    }
  },
}

let container = document.getElementById('root')
let root = createRoot(container as Element | DocumentFragment)

root.render(
  <Auth0Provider {...auth0ProviderConfig}>
    <StyledEngineProvider injectFirst>
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <RouterProvider router={router} />
        </ThemeProvider>
      </Provider>
    </StyledEngineProvider>
  </Auth0Provider>,
)
